<template>
  <div class="panel-wrap">
    <div class="section-note">*基础信息（与账号绑定后，不可修改）</div>
    <div class="readonly-grid">
      <div class="field field--readonly">
        <label>姓名</label>
        <div class="readonly-input">{{ form.basic.name || '--' }}</div>
      </div>
      <div class="field field--readonly">
        <label>性别</label>
        <div class="readonly-input">{{ form.basic.gender || '--' }}</div>
      </div>
      <div class="field field--readonly">
        <label>年龄</label>
        <div class="readonly-input">{{ form.basic.age || '--' }}</div>
      </div>
      <div class="field field--readonly">
        <label>手机号码</label>
        <div class="readonly-input">{{ form.basic.phone || '--' }}</div>
      </div>
      <div class="field field--readonly">
        <label>身份证号</label>
        <div class="readonly-input">{{ form.basic.idCard || '--' }}</div>
      </div>
      <div class="field field--readonly">
        <label>依从性</label>
        <div class="readonly-input">{{ complianceText }}</div>
      </div>
    </div>

    <div class="section-note section-note--edit">*以下补充信息（可修改）</div>
    <div class="edit-grid">
      <div class="field">
        <label>职业</label>
        <a-input v-model:value="jobValue" placeholder="请输入职业" maxlength="10" />
      </div>
      <div class="field">
        <label>婚姻状况</label>
        <a-select v-model:value="marriageValue" placeholder="请选择" :options="marriageOptions" />
      </div>
      <div class="field field--full field--tag-row">
        <label>诊断标签</label>
        <div class="chip-list">
          <span
            v-for="tag in diagnosisDefaultTags"
            :key="tag"
            class="chip"
            :class="{
              'chip--active': isTagActive(tag),
              'chip--disabled': !canToggleTag(tag)
            }"
            @click="onTagClick(tag)"
          >
            {{ tag }}
          </span>
        </div>
      </div>
      <div class="field field--full field--textarea">
        <a-textarea
          v-model:value="diagnosisManualTagsValue"
          :maxlength="20"
          :auto-size="{ minRows: 5, maxRows: 5 }"
          placeholder="请输入内容"
          :disabled="diagnoseRemarkDisabled"
          style="margin-top: -10px"
        />
        <div class="field-count">
          {{ diagnosisManualTagsValue ? String(diagnosisManualTagsValue.length) : 0 }}/20
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps } from 'vue'
import { createFieldModel } from '../../hooks/useFieldModel'

const props = defineProps({
  form: {
    type: Object,
    default: () => ({})
  },
  marriageOptions: {
    type: Array,
    default: () => []
  },
  complianceOptions: {
    type: Array,
    default: () => []
  },
  diagnosisDefaultTags: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['toggle-tag', 'update-field'])

const jobValue = createFieldModel(() => props.form, 'basic', 'job', emit)
const marriageValue = createFieldModel(() => props.form, 'basic', 'marriage', emit, {
  select: true
})
const diagnosisManualTagsValue = createFieldModel(
  () => props.form,
  'basic',
  'diagnosisManualTags',
  emit
)

const selectedDiagnoseTagCount = computed(() => {
  if (!Array.isArray(props.form?.basic?.diagnosisTags)) return 0
  return props.form.basic.diagnosisTags.length
})

const hasDiagnoseRemark = computed(() =>
  Boolean((props.form?.basic?.diagnosisManualTags || '').trim())
)
const diagnoseRemarkDisabled = computed(() => selectedDiagnoseTagCount.value >= 2)
const complianceText = computed(() => {
  const compliance = props.form?.basic?.compliance
  const option = props.complianceOptions.find(item => item.value === compliance)
  return props.form?.basic?.complianceLabel || option?.label || compliance || '--'
})

const isTagActive = tag => props.form?.basic?.diagnosisTags?.includes(tag)

const canToggleTag = tag => {
  if (isTagActive(tag)) return true
  if (selectedDiagnoseTagCount.value >= 2) return false
  if (hasDiagnoseRemark.value && selectedDiagnoseTagCount.value >= 1) return false
  return true
}

const onTagClick = tag => {
  if (!canToggleTag(tag)) return
  emit('toggle-tag', tag)
}
</script>

<style lang="scss" scoped>
.panel-wrap {
  padding-top: 8px;
}

.section-note {
  color: #ff4d4f;
  font-size: 14px;
  line-height: 20px;
  margin-bottom: 12px;
}

.section-note--edit {
  margin-top: 12px;
}

.readonly-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 24px;
}

.edit-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px 16px;
}

.field {
  min-width: 0;
  box-sizing: border-box;
  overflow: visible;

  label {
    display: block;
    margin-bottom: 8px;
    color: #333;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
  }

  .readonly-input {
    min-height: 36px;
    padding: 7px 12px;
    border: 1px solid #ececec;
    border-radius: 4px;
    background: #fafafa;
    color: #a3a3a3;
    font-size: 14px;
    line-height: 20px;
    display: flex;
    align-items: center;
    box-sizing: border-box;
  }

  :deep(.ant-input),
  :deep(.ant-select-selector),
  :deep(.ant-input-textarea textarea) {
    height: 36px;
    border: 1px solid #e7e7e7;
    border-radius: 4px;
    box-shadow: none;
    padding: 5px 12px;
    color: #333;
    font-size: 14px;
    line-height: 20px;
    background: #fff;
    box-sizing: border-box;
  }

  :deep(.ant-select) {
    width: 100%;
    max-width: 100%;
    flex-shrink: 0;
  }

  :deep(.ant-select-selector) {
    width: 100% !important;
    max-width: 100%;
    padding-left: 12px !important;
    padding-right: 8px !important;
    overflow: hidden;
  }

  :deep(.ant-select-clear) {
    right: 8px;
  }

  :deep(.ant-select-arrow) {
    right: 4px;
  }

  :deep(.ant-select-selection-item) {
    line-height: 26px;
  }

  :deep(.ant-input::placeholder),
  :deep(.ant-input-textarea textarea::placeholder) {
    color: #b8b8b8;
  }

  :deep(.ant-select-selection-placeholder) {
    color: #b8b8b8;
    line-height: 26px;
  }
}

.field--full {
  grid-column: 1 / -1;
}

.field--textarea {
  position: relative;

  :deep(.ant-input-textarea textarea) {
    height: 122px !important;
    padding-top: 8px;
    resize: none;
  }
}

.field--tag-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;

  label {
    flex: 0 0 auto;
    margin-bottom: 0;
    line-height: 28px;
  }
}

.field-count {
  position: absolute;
  right: 12px;
  bottom: 12px;
  color: #b7b7b7;
  font-size: 12px;
  line-height: 16px;
  pointer-events: none;
}

.chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip {
  min-height: 28px;
  padding: 4px 12px;
  border-radius: 4px;
  border: 1px solid #e8e8e8;
  background: #f5f5f5;
  color: #808080;
  font-size: 13px;
  line-height: 18px;
  cursor: pointer;
  box-sizing: border-box;
}

.chip--disabled {
  color: #bbb;
  border-color: #e8e8e8;
  background: #fafafa;
  cursor: not-allowed;
}

.chip--active {
  color: #1677ff;
  border-color: #b7d6ff;
  background: #eef5ff;
}
</style>
